import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import Download from "@mui/icons-material/Download";
import { Button, CircularProgress, TextField } from "@mui/material";
import "./QAForm.css";
import ResultColumn from "./ResultColumn";
import ResultColumnInbound from "./ResultColumnInbound";
import "../Components/Components.css";
import Loader from "../Components/Loader";
import { getQAForm, saveQAForm } from "../Store/Actions/TicketAction";

const QAForm = ({
  caseID,
  channelID,
  filledComment,
  filledTotalScore,
  isRecordingLoading,
  isRecordingPlaying,
  recordingRef,
  scoreItemID,
  selectedRecording,
  setIsNewWindowOpen,
  setIsRecordingPlaying,
}) => {
  const [isSeekVoiceAllowed, setIsSeekVoiceAllowed] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState(null);

  // Sum of all question weights
  const [totalScore, setTotalScore] = useState(
    filledTotalScore ? filledTotalScore : 0
  );

  // Max amount of score attainable
  const [maxScore, setMaxScore] = useState(0);

  // List of individual question score
  const [questionScores, setQuestionScores] = useState([]);

  const [formObject, setFormObject] = useState({});
  const [comment, setComment] = useState(filledComment ? filledComment : "");
  const { agentPermission, myInfo } = useSelector((state) => state.auth);

  // Dummy question used to render on first load (fix CSS not loading on first load)
  const dummyQuestion = {
    scorequestionpartid: 1,
    scorequestionid: 1,
    questionindex: 1,
    question: "Loading...",
    weight: 2,
    requiredscore: 0,
    score: 100,
    remarks: null,
    choice: null,
  };

  useEffect(() => {
    getQAForm(
      myInfo.user.ClientID ? myInfo.user.ClientID : 0,
      scoreItemID,
      channelID
    )
      .then((response) => {
        if (response.status) {
          process.env.NODE_ENV === "development" &&
            console.log("QA Form", response.data);

          setForm(response.data);

          const sumScore = response.data.formparts.reduce(
            (accumulator, currentValue) =>
              accumulator + currentValue.totalweight,
            0
          );

          setMaxScore(sumScore);
          !filledTotalScore && setTotalScore(sumScore);

          setQuestionScores(
            response.data.questionlist.map((question) => ({
              id: question.scorequestionid,
              score: question.score,
              choice: question.choice,
            }))
          );

          let tempFormObject = {
            scoreFormID: response.data.scoreformid,
            requiredQAScore: response.data.requiredscore,
            totalScore: sumScore,
            comment: comment,
          };

          response.data.formparts.forEach((formpart) => {
            tempFormObject[`partScoreVal_${formpart.scorequestionpartid}`] =
              formpart.totalweight;
          });

          response.data.questionlist.forEach((question) => {
            tempFormObject[`scoreQuestionID|${question.scorequestionid}`] =
              JSON.stringify({
                score: question.score,
              });
          });

          setFormObject(tempFormObject);
        } else {
          console.log("error", response.error);

          toast.error("Error loading QA form: " + response.error, {
            autoClose: 3000,
          });
        }
      })
      .catch((err) => {
        toast.error("Error loading QA form: " + err, {
          autoClose: 3000,
        });

        console.log("err", err);
      });
  }, []);

  // Get seek voice permission
  useEffect(() => {
    if (channelID === "1") {
      const seekVoicePermission = agentPermission.find(
        (permission) => permission.PermissionCode === "User.SeekVoice"
      );

      if (seekVoicePermission && seekVoicePermission.PermissionAccess === "A") {
        setIsSeekVoiceAllowed(true);
      } else {
        setIsSeekVoiceAllowed(false);
      }
    }
  }, [agentPermission]);

  const renderRating = () => {
    let className, icon, rating;

    if (totalScore <= maxScore / 3) {
      className = "bad";
      icon = "bxs:dislike";
      rating = "Bad";
    } else if (totalScore <= maxScore / 2) {
      className = "good";
      icon = "bxs:like";
      rating = "Good";
    } else {
      className = "excellent";
      icon = "bxs:like";
      rating = "Excellent";
    }

    return (
      <div className={`total-score ${className}`}>
        <div className="score">
          <p className="text">TOTAL SCORE</p>
          <p className="value">{totalScore}</p>
        </div>

        <div className="rating">
          <Icon icon={icon} width="40" height="40" />
          <p>{rating}</p>
        </div>
      </div>
    );
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    let tempFormObject = formObject;
    tempFormObject.totalScore = totalScore;
    tempFormObject.comment = comment;

    questionScores.forEach((question) => {
      tempFormObject[`scoreQuestionID|${question.id}`] = JSON.stringify({
        score: question.score,
        choice: question.choice,
      });
    });

    process.env.NODE_ENV === "development" &&
      console.log("tempFormObject", tempFormObject);

    setFormObject(tempFormObject);

    const payload = {
      caseid: caseID,
      channelid: channelID,
      qaformstring: JSON.stringify(tempFormObject),
      scoreitemid: scoreItemID,
      scoringby: myInfo.user.AgentID,
    };

    saveQAForm(payload)
      .then((response) => {
        if (response.status) {
          console.log("Output data", response.data);

          toast.success("QA form saved successfully", {
            autoClose: 3000,
          });

          setIsSubmitting(false);
          setIsNewWindowOpen(false);
        } else {
          console.log("error", response.error);

          toast.error("Error saving QA form: " + response.error, {
            autoClose: 3000,
          });

          setIsSubmitting(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        toast.error("Error saving QA form: " + err, { autoClose: 3000 });
        setIsSubmitting(false);
      });
  };

  return (
    <div className="qa-form">
      <div className="dialog-container">
        <form method="post">
          <div className="dialog-content">
            <header>
              <h2 className="title">QA Form</h2>

              {selectedRecording ? (
                <div
                  className={`controls new-window ${
                    !isSeekVoiceAllowed ? "simple" : ""
                  }`}
                >
                  {isSeekVoiceAllowed ? (
                    // Audio with controls if seek voice is allowed
                    <audio
                      controls
                      controlsList="nodownload"
                      src={
                        selectedRecording
                          ? `data:audio/wav;base64,${selectedRecording?.filebytes}`
                          : ""
                      }
                    />
                  ) : (
                    // Play/pause button if seek voice is not allowed
                    <Button
                      className="download-button"
                      disabled={!selectedRecording || isRecordingLoading}
                      variant="contained"
                      onClick={() => {
                        if (isRecordingPlaying) {
                          recordingRef.current.pause();
                          setIsRecordingPlaying(false);
                        } else {
                          recordingRef.current.play();
                          setIsRecordingPlaying(true);
                        }
                      }}
                    >
                      {isRecordingLoading ? (
                        <Icon height="24" icon="eos-icons:loading" />
                      ) : isRecordingPlaying ? (
                        <Icon height="24" icon="ic:round-pause" />
                      ) : (
                        <Icon height="24" icon="ph:play-fill" />
                      )}
                    </Button>
                  )}

                  {selectedRecording && (
                    <a
                      download={selectedRecording?.filename}
                      href={`data:audio/wav;base64,${selectedRecording?.filebytes}`}
                    >
                      <Button variant="contained" className="download-button">
                        <Download />
                      </Button>
                    </a>
                  )}
                </div>
              ) : channelID === "1" ? (
                <Loader desc={"Loading Recording"} />
              ) : (
                ""
              )}
            </header>

            <table>
              <thead>
                <tr>
                  <th>No</th>
                  <th>Question</th>
                  <th>Weight</th>
                  <th>Result</th>
                </tr>
              </thead>

              <tbody>
                {form ? (
                  form.formparts
                    // Sorting to make sure parts are in order
                    .sort((a, b) => a.partindex - b.partindex)

                    // Map the sorted formParts
                    .map((formPart) => (
                      <Fragment key={formPart.scorequestionpartid}>
                        {formPart.title && (
                          <tr>
                            <td className="section-name" colSpan="4">
                              {`${formPart.title} - ${formPart.totalweight}`}
                            </td>
                          </tr>
                        )}

                        {form.questionlist
                          // Filter questions according to formPart
                          .filter(
                            (question) =>
                              question.scorequestionpartid ===
                              formPart.scorequestionpartid
                          )

                          // Sorting to make sure questions are in order
                          .sort((a, b) => a.questionindex - b.questionindex)

                          // Map the filtered and sorted questions
                          .map((question) => (
                            <tr key={question.scorequestionid}>
                              <td>{question.questionindex}</td>
                              <td>{question.question}</td>
                              <td>{question.weight}</td>

                              <td>
                                {channelID ? (
                                  <ResultColumnInbound
                                    question={question}
                                    setTotalScore={setTotalScore}
                                    questionScores={questionScores}
                                    setQuestionScores={setQuestionScores}
                                  />
                                ) : (
                                  <ResultColumn
                                    question={question}
                                    totalScore={totalScore}
                                    setTotalScore={setTotalScore}
                                    questionScores={questionScores}
                                    setQuestionScores={setQuestionScores}
                                  />
                                )}
                              </td>
                            </tr>
                          ))}
                      </Fragment>
                    ))
                ) : (
                  // Render dummy question when data is loading
                  <tr key={dummyQuestion.scorequestionid}>
                    <td>{dummyQuestion.questionindex}</td>
                    <td>{dummyQuestion.question}</td>
                    <td>{dummyQuestion.weight}</td>

                    <td>
                      <ResultColumn
                        question={dummyQuestion}
                        totalScore={totalScore}
                        setTotalScore={setTotalScore}
                        questionScores={questionScores}
                        setQuestionScores={setQuestionScores}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Hide when data is loading */}
            {form && (
              <div className="footer">
                <div className="info">
                  {/* <TextField label="POE" select value="1">
                    <MenuItem value="1">POE</MenuItem>
                  </TextField> */}

                  <TextField
                    label="Comments"
                    minRows={2}
                    multiline
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </div>

                <div className="summary">
                  {renderRating()}

                  <section className="buttons">
                    <button
                      className="cancel"
                      type="button"
                      onClick={() => setIsNewWindowOpen(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className="confirm"
                      disabled={isSubmitting}
                      type="button"
                      onClick={() => handleSubmit()}
                    >
                      {isSubmitting && (
                        <CircularProgress className="progress" />
                      )}
                      Save
                    </button>
                  </section>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default QAForm;
